import { send } from './client';

/**
 * The picker lookups — `GET /lookups/staff` and `GET /lookups/roles`.
 *
 * ## Why not `usersApi.list()` / `rolesApi.list()`
 *
 * Those need `user.view` / `user.role.assign`, which the people filing
 * complaints and configuring the escalation chain do not hold — which is why
 * those screens used to ask for raw UUIDs. These two endpoints return just
 * enough to pick someone, to exactly the callers whose screens need it (the
 * API's `App\Policies\LookupPolicy` lists them). Shapes are verified against
 * `StaffLookupResource` / `RoleLookupResource` and the `StaffLookup` /
 * `RoleLookup` schemas in the API's `ApiSpecification`.
 *
 * Both answer a bare `{ data: [...] }` with no `meta`, so `send()` is right.
 */

/** One person, as a picker offers them. Never carries email, phone or roles. */
export interface StaffLookup {
  uuid: string;
  full_name: string;
  username: string;
}

/** One role, both labels so a language switch needs no refetch. */
export interface RoleLookup {
  uuid: string;
  name: string;
  label_en: string;
  label_ar: string;
}

export interface StaffLookupQuery {
  /** Case-insensitive substring of full name or username. Blank = first page alphabetically. */
  q?: string;
  /** Default 20, maximum 50 server-side (a 422 above it). */
  limit?: number;
}

function staffPath(query: StaffLookupQuery): string {
  const params = new URLSearchParams();
  const q = query.q?.trim() ?? '';
  if (q !== '') params.set('q', q);
  if (query.limit !== undefined) params.set('limit', String(query.limit));
  const qs = params.toString();
  return qs === '' ? '/lookups/staff' : `/lookups/staff?${qs}`;
}

export const lookupsApi = {
  staff: (query: StaffLookupQuery = {}, signal?: AbortSignal) =>
    send<readonly StaffLookup[]>(staffPath(query), { signal }),

  roles: (signal?: AbortSignal) => send<readonly RoleLookup[]>('/lookups/roles', { signal }),
};
